import { ChatMessage, ChatParticipant, Prisma } from '@prisma/client'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Api } from '~/core/trpc'

type Message = Prisma.ChatMessageGetPayload<{
  include: {
    participant: true
    children: {
      orderBy: {
        createdAt: 'desc'
      }
      take: 1
    }
    _count: {
      select: {
        children: true
      }
    }
  }
}>

type Props = {
  conversationId: string
  containerId: string
  messageParent?: ChatMessage
  participantUser?: ChatParticipant
  refList?: React.RefObject<HTMLElement>
}

function useFetch({
  conversationId,
  parentId,
  containerId = 'feed__infinite-scroll-container',
}) {
  const countItemsByPages = 10

  const api = Api.useUtils()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetched,
    isFetching,
    isLoading,
  } = useInfiniteQuery<{ items: ChatMessage[]; nextCursor: Date | null }>(
    ['chatMessages', conversationId, parentId],
    async ({ pageParam = null }) => {
      try {
        const response = await api.chatCustom.findManyMessages.fetch({
          conversationId,
          parentId: parentId ?? null,
          cursor: pageParam ?? null,
          take: countItemsByPages,
        })

        return response
      } catch (error) {
        throw new Error('Failed to load messages', error)
      }
    },

    {
      getNextPageParam: lastPage => lastPage.nextCursor,
    },
  )

  const pages: ChatMessage[][] = data?.pages.map(page => page.items) ?? []

  const hash = pages
    .flat()
    .map(item => item.id + item.parentId + item['_count']?.children)
    .join()

  const items = useMemo(() => {
    return pages.map(page => page.slice(0, countItemsByPages)).flat()
  }, [hash])

  return {
    items,
    isFetching: isFetching || isLoading,
    isFetched: isFetched,
    fetchNextPage,
    hasNextPage,
    refetch,
    containerId,
  }
}

export const useMessagesV2 = ({
  conversationId,
  containerId,
  messageParent,
  participantUser,
}: Props) => {
  const deleteMutation = Api.chatMessage.delete.useMutation()

  const [messageOptimist, setMessageOptimist] = useState<Message>()

  const isParentHidden = messageParent?.values?.['skip']

  const hook = useFetch({
    containerId,
    conversationId,
    parentId: messageParent ? messageParent.id : null,
  })

  const onMessageSent = _ => {}

  const onMessageSentOptimist = (message: ChatMessage) => {
    if (participantUser) {
      setMessageOptimist({
        ...message,
        participant: participantUser,
        participantId: participantUser.id,
        children: [],
        _count: {
          children: 0,
        },
      })
    }
  }

  useEffect(() => {
    const canClear =
      messageOptimist &&
      hook.items.find(item => item.idTemporary === messageOptimist.id)

    if (canClear) {
      setMessageOptimist(null)
    }
  }, [messageOptimist, hook.items])

  const canIncludeParent = messageParent && !isParentHidden && !hook.hasNextPage

  const messages = useMemo(() => {
    const items = [...(hook.items ?? [])]

    const canAddOptimist =
      messageOptimist &&
      !hook.items.find(item => item.idTemporary === messageOptimist.id)

    if (canAddOptimist) {
      items.unshift(messageOptimist)
    }

    if (canIncludeParent) {
      return [
        ...items,
        { ...messageParent, _count: { children: 0 }, children: [] },
      ]
    }

    return items
  }, [hook.items, hook.hasNextPage, canIncludeParent, messageOptimist])

  const deleteMessage = async (chatMessage: ChatMessage) => {
    try {
      await deleteMutation.mutateAsync({
        where: {
          id: chatMessage.id,
        },
      })

      hook.refetch()
    } catch (error) {
      console.error('Error deleting message:', error)
    }
  }

  return {
    isReady: hook.isFetched,
    messages: messages,
    hook,
    deleteMessage,
    onMessageSent,
    onMessageSentOptimist,
  }
}
